import { bankServiceMapping } from 'Utility/bankServiceMapping'
import { DEFAULT_BANK, TENANT_FOR_THEME } from 'Schemas/constants'
import { useSearchParams } from 'react-router-dom'

/**
 * Extracts the first segment of the current URL path, which is typically used to determine the theme.
 *
 * @returns {string|null} The first path segment if present, otherwise null.
 */
export const getBankFromPath = () => {
  const match = window.location.pathname.match(/^\/([^/]+)/)
  const bank = match?.[1]
  const validBank = bank && typeof bank === 'string' && Object.keys(bankServiceMapping).includes(bank.toLowerCase())
    ? bank.toLowerCase()
    : ''
  return bank ? validBank : ''
}

/**
 * Resolves the current bank from the route, or from the login tenant query parameter when the route has no bank.
 *
 * @returns {string} The mapped bank identifier, or an empty string when no valid bank is available.
 */
export const getBankFromContext = () => {
  const bankFromPath = getBankFromPath()
  const [searchParams] = useSearchParams()

  if (bankFromPath) {
    return bankFromPath
  }

  const tenant = searchParams.get('tenant')
  if (tenant === null) {
    return window.sessionStorage.getItem('activeTheme') || ''
  }

  return TENANT_FOR_THEME[tenant] || ''
}

/**
 * Checks if the given bank is valid based on the skin mapping.
 * @param {string} bank - The bank to be checked.
 * @returns {string} - The valid bank or the default value 'NEXI'.
 */
export const checkSkinMapping = (bank) =>
  bank && typeof bank === 'string' && Object.keys(bankServiceMapping).includes(bank.toLowerCase())
    ? bank.toLowerCase()
    : DEFAULT_BANK

/**
 * Retrieves the object skin mapping based on the provided bank, element, and types.
 * If the mapping is found, it returns the corresponding value.
 * If the mapping is not found, it returns the value for the provided element.
 *
 * @param {string} bank - The bank identifier.
 * @param {string} element - The element identifier.
 * @param {...string} types - Additional types to search for in the mapping.
 * @returns {*} - The value from the object skin mapping.
 */
export const getObjSkinMapping = (bank, element, ...types) => {
  const checkBank = checkSkinMapping(bank)
  const result = [element, ...types].reduce((acc, type) => acc?.[type], bankServiceMapping[checkBank])
  return result !== undefined ? result : bankServiceMapping[checkBank]?.[element]
}
